type Item = {
  label: string;
  value: number | null;
  items?: Item[];
};

type ResponseType = {
  items: Item[];
  total: number | null;
  resultado?: string;
};

export const parseDetailText = (
  detalle: string | undefined,
  startText?: string,
  resultado?: string
): ResponseType | null => {
  if (!detalle) return null;
  let lines = detalle.split('\r\n').filter((line) => line?.trim() !== '');

  if (startText) {
    const firstValidLine = lines.findIndex((line) => line?.includes(startText));
    lines = lines.slice(firstValidLine);
  }

  const parsStringValue = (value: string): number | null => {
    const parsedValue = (value || '')
      .replaceAll('U$S', '')
      .replace(/\./g, '')
      .replace(',', '.')
      .trim();

    return Number.isNaN(Number(parsedValue)) ? null : Number(parsedValue);
  };

  const items = lines
    .map((line, i): Item | null => {
      const [label, amount] = line.split('U$S');
      let childs: Item[] = [];
      const dateRegex = /\d{2}\/\d{2}\/\d{4}/;

      let value: number | null = null;

      if (!amount?.trim() || label.includes('Manipuleo')) {
        const nextLines = lines.slice(i + 1);
        const endIndex = nextLines.findIndex((curr) => !dateRegex.test(curr));
        const childLines = nextLines.slice(0, endIndex);

        childs = childLines.map((childLine) => {
          const separator = childLine.split('U$S')[1] || '';
          const [childLabel, childAmount] = childLine.split(separator);

          return {
            label: separator
              ? `${childLabel?.trim()} ${separator.trim()}`
              : childLine?.trim(),
            value: parsStringValue(childAmount || separator),
          };
        });

        const manipuleoMatch = line.match(
          /Manipuleo de contenedores\s+U\$S\s+([\d,.]+)/
        );
        if (manipuleoMatch) {
          value = parseFloat(
            manipuleoMatch[1].replace('.', '').replace(',', '.')
          ); // Ajustar el formato del valor
        }

        if (childs.length > 0) {
          const childsSum = childs.reduce(
            (acc, curr) => acc + (curr.value || 0),
            0
          );
          value = (value || 0) + childsSum;
        }
        if (value === 0) {
          value = null;
        }
      } else {
        value = parsStringValue(amount);
      }

      if (dateRegex.test(label)) return null;

      return {
        label: label?.trim(),
        value,
        items: childs,
      };
    })
    .filter((item) => item !== null) as Item[];

  const totalLineIndex = items.findIndex((item) => {
    return item?.label.toLowerCase().includes('total');
  });

  const totalLine = items[totalLineIndex];

  const itemsWithoutTotal = [
    ...items.slice(0, totalLineIndex),
    ...items.slice(totalLineIndex + 1),
  ];

  return {
    items: itemsWithoutTotal,
    total: totalLine.value,
    resultado: resultado || '',
  };
};

export type Value = {
  detalle: string;
  error: string;
  factura: {
    lineas: {
      detalle: {
        tarifaValor: number;
        texto: string;
        importe: number;
      }[];
      importe: number;
      textoImpresion: string;
    }[];
  };
  total: number;
  resultado: string;
};

const parseAmount = (amount: number): string =>
  (amount || 0)?.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

type ParseInvoiceDataParams = {
  data: Value;
  usarFactura?: boolean;
};
export const parsePrecintoData = (resultado: string): Item[] => {
  if (!resultado) return [];

  const lines = resultado.split('\n').filter((line) => line.trim() !== '');
  const buqueItem: Item[] = [];
  const armadorItem: Item[] = [];
  const llegadaItem: Item[] = [];
  const precintoItems: Item[] = [];
  let precintoCount = 0;

  lines.forEach((line) => {
    const trimmedLine = line.trim();

    if (trimmedLine.startsWith('PRECINTO')) {
      const precintoValue = trimmedLine.replace('PRECINTO', '').trim();
      if (precintoValue) {
        precintoCount += 1;
        precintoItems.push({
          label: `PRECINTO ${precintoCount}`,
          value: null,
          items: [
            {
              label: precintoValue,
              value: null,
              items: [],
            },
          ],
        });
      }
    } else if (
      trimmedLine.includes('BUQUE') ||
      trimmedLine.includes('LLEGADA') ||
      trimmedLine.includes('ARMADOR')
    ) {
      const [key, ...valueParts] = trimmedLine.split(/\s{2,}/);
      const value = valueParts.join(' ').trim();

      if (key && value) {
        const item = {
          label: key,
          value: null,
          items: [
            {
              label: value,
              value: null,
              items: [],
            },
          ],
        };

        if (key === 'BUQUE') {
          buqueItem.push(item);
        } else if (key === 'ARMADOR') {
          armadorItem.push(item);
        } else if (key === 'LLEGADA') {
          llegadaItem.push(item);
        }
      }
    }
  });

  return [...buqueItem, ...armadorItem, ...llegadaItem, ...precintoItems];
};

export const parseInvoiceData = ({
  data,
  usarFactura = false,
}: ParseInvoiceDataParams): ResponseType | null => {
  if (!data) return null;
  const { lineas } = data?.factura || {};
  if (!usarFactura || !Array.isArray(lineas))
    return parseDetailText(data?.detalle, undefined, data?.resultado);

  const items = lineas
    .map((linea) => {
      const subitems = linea.detalle.map(({ tarifaValor, importe, texto }) => {
        const valor = parseAmount(tarifaValor);

        const breakLineAtEnd = texto.endsWith('\r\n');
        const cleanedText = breakLineAtEnd ? texto.slice(0, -2) : texto;
        const textParts = cleanedText.split('\r\n');
        const currentLabel = textParts[textParts.length - 1]?.trim() || '';
        const textWithoutValue = currentLabel.split('U$S')[0]?.trim() || '';

        const label = `${textWithoutValue}  ${tarifaValor ? `U$S ${valor}` : ''}`;

        return {
          label,
          value: importe,
          items: [],
        };
      });

      return {
        label: (linea?.textoImpresion || '')
          .split('\r\n')[0]
          ?.split('U$S')[0]
          ?.trim(),
        value: linea?.importe || null,
        items: subitems?.length > 1 ? subitems : [],
      };
    })
    .filter((item) => item?.label);

  return { items, total: data?.total || 0, resultado: data?.resultado || '' };
};
